import NavArrow from '@/assets/navArrow.svg';
import { IconRenderer, Link, ResponsiveImage } from '@/components';
import tailwindConfig from '@/tailwind.config';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import NavColumn from './NavColumn';
import { selectCampaignColor, selectCampaignIcon } from '@/store';
import { useSelector } from 'react-redux';
import { isColorDark } from '@/utils';
import Star from '../../assets/star.svg';

const CampaignNavItem = ({
  navItem,
  level,
  parentIndex,
  hasTwoColumn,
  activeMenu,
  setActiveMenu,
  unsetActiveMenu,
  mainNavTitle,
  close,
  parentMenuName,
  isGlobalNavItem,
}) => {
  const _setActiveMenu = (sub) => {
    let newValue = {};
    newValue[`l${level + 1}`] = sub;
    setActiveMenu(newValue);
  };
  const _setActiveMenuByHover = (sub) => {
    if (window.innerWidth > parseInt(tailwindConfig.theme.screens.lg, 10)) {
      _setActiveMenu(sub);
    }
  };
  const _unsetActiveMenu = () => {
    if (window.innerWidth > parseInt(tailwindConfig.theme.screens.lg, 10)) {
      unsetActiveMenu();
    }
  };

  const campaignColor = useSelector(selectCampaignColor);

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(true);
  }, []);

  const linkClass = classNames(
    'link-element font-medium text-sm leading-[22px] text-primary cursor-pointer flex items-center justify-between duration-500 [&_svg]:duration-500',
    'text-primary ',
    level === 2 ? 'hover:text-white [&_svg]:hover:fill-white' : 'hover:text-primary',
    activeMenu && activeMenu[`l${1}`] && navItem.flyoutNavigation?.length > 0 ? 'active' : '',
    activeMenu && activeMenu[`l${level + 1}`] && navItem.flyoutNavigation?.length > 0
      ? level === 2
        ? 'lg:text-brand-soft-white lg:[&_svg]:fill-brand-soft-white'
        : 'lg:text-primary'
      : level === 2
        ? 'lg:text-brand-soft-white lg:[&_svg]:fill-brand-soft-white'
        : 'lg:text-primary',
  );

  return (
    <>
      <div>
        {navItem.flyoutNavigation?.length > 0 ? (
          <div
            className={classNames(
              'campaign-nav-item bg-secondary-coolsand text-primary py-7 px-10 flex flex-col gap-3 cursor-pointer',
            )}
            onClick={() => _setActiveMenu({})}
            onMouseEnter={() => {
              _setActiveMenuByHover({});
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {navItem.campaignMode && (
                  <IconRenderer selector={selectCampaignIcon} fallback={<Star />} className="fill-primary size-5" />
                )}
                {navItem.navItemText && (
                  <span className="text-sm leading-6 font-medium">
                    {navItem.navItemText || navItem.navigationalTitle}
                  </span>
                )}
              </div>
              {navItem.flyoutNavigation?.length > 0 && (
                <NavArrow className={classNames(isGlobalNavItem ? '[&_path]:!stroke-white' : '')} role="presentation" />
              )}
            </div>
            {navItem.navItemDescription && <p className="text-xs max-w-[249px]">{navItem.navItemDescription}</p>}
            {navItem.navItemImage && (
              <ResponsiveImage
                image={navItem.navItemImage}
                widths={{ xs: 420, sm: 420, md: 420, lg: 586, xl: 586, xxl: 586 }}
                heights={{ xs: 165, sm: 165, md: 165, lg: 230, xl: 230, xxl: 230 }}
                className="!h-[110px] xxl+:!h-auto xxl+:aspect-[28/11] xxl+:max-h-[230px]"
              />
            )}
          </div>
        ) : navItem?.linkElements?.length > 0 ? (
          <div className={`flex flex-col gap-4 ${navItem.linkElements.length === 1 ? 'break-inside-avoid' : ''}`}>
            <div className="text-primary font-normal text-sm leading-6">{navItem.navigationalTitle}</div>
            <div className="flex flex-col gap-4">
              {navItem.linkElements.map((link, index) => (
                <div key={index}>
                  <Link
                    className={`${linkClass} w-fit xl:[&:hover+.image-block]:opacity-100`}
                    link={link.link}
                    onClick={close}
                  >
                    {link.link?.text}
                  </Link>

                  {loaded && link.image && window.innerWidth > parseInt(tailwindConfig.theme.screens.xl, 10) && (
                    <div
                      className={`image-block absolute w-1/4 h-full top-0 right-0 transition-opacity duration-500  xxl:z-[-1] opacity-0
                      ${hasTwoColumn ? 'translate-x-[-100%]' : 'translate-x-[-200%]'}
                      `}
                      style={{ zIndex: (parentIndex + 1 + index + 1) * -1 }}
                    >
                      <ResponsiveImage
                        dataNotLazy
                        image={link.image}
                        widths={{ xs: window.innerWidth / 4 }}
                        heights={{ xs: window.innerHeight }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {navItem.ctaLink && (
              <Link className={`${linkClass} underline`} link={navItem.ctaLink} onClick={close}>
                {navItem.ctaLink.text}
              </Link>
            )}
          </div>
        ) : navItem.isFestiveLink ? (
          <Link
            link={navItem.navItemLink}
            onClick={close}
            className={classNames(
              'link-element font-medium text-sm leading-[22px] cursor-pointer flex items-center py-5 justify-between duration-500 [&_svg]:duration-500',
              isColorDark(campaignColor) ? 'text-white' : 'text-primary',
            )}
            style={{
              backgroundColor: campaignColor,
              boxShadow: `-40px 0px 0px 0px ${campaignColor}, 42px 0px 0px 0px ${campaignColor}`,
            }}
          >
            <div className="flex items-center gap-2">
              <IconRenderer selector={selectCampaignIcon} className="size-5 fill-current" />
              {navItem?.navItemText || navItem.navItemLink?.text || navItem.navigationalTitle || navItem.ctaLink?.text}
            </div>
          </Link>
        ) : (
          <Link
            link={
              navItem.navItemLink ||
              (navItem.ctaLink && {
                ...navItem.ctaLink,
                text: navItem.navigationalTitle || navItem.navItemText || navItem.ctaLink.text,
              })
            }
            className="campaign-nav-item bg-secondary-coolsand text-primary py-7 px-10 flex flex-col gap-3"
            onClick={close}
            onMouseEnter={() => _unsetActiveMenu()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {navItem.campaignMode && (
                  <IconRenderer selector={selectCampaignIcon} fallback={<Star />} className="fill-primary size-5" />
                )}
                <span className="text-sm leading-6 font-medium">
                  {navItem?.navItemText ||
                    navItem.navItemLink?.text ||
                    navItem.navigationalTitle ||
                    navItem.ctaLink?.text}
                </span>
              </div>
            </div>
            {navItem.navItemDescription && <p className="text-xs max-w-[249px]">{navItem.navItemDescription}</p>}
            {navItem.navItemImage && (
              <ResponsiveImage
                image={navItem.navItemImage}
                widths={{ xs: 420, sm: 420, md: 420, lg: 525, xl: 525, xxl: 525 }}
                heights={{ xs: 165, sm: 165, md: 165, lg: 207, xl: 207, xxl: 207 }}
                className="!h-[110px]"
              />
            )}
          </Link>
        )}

        {(!navItem.flyoutNavigation || navItem.flyoutNavigation.length === 0) &&
          !(navItem.navItemLink || navItem.ctaLink) &&
          navItem.linkElements?.length === 0 && (
            <div className={linkClass} onClick={() => _setActiveMenu({})}>
              {navItem.navItemText || navItem.navigationalTitle}
            </div>
          )}
      </div>

      {navItem.flyoutNavigation?.length > 0 && (
        <NavColumn
          navItems={navItem.flyoutNavigation}
          mainNavTitle={mainNavTitle}
          flyoutNavigationBottom={navItem.flyoutNavigationBottom}
          flyouHead={navItem.navItemLink}
          isHotelListing={navItem.isHotelListing}
          allHotels={navItem.allHotels}
          detailedNavItems={navItem.detailedNavItems}
          level={level + 1}
          activeMenu={activeMenu}
          setActiveMenu={(sub) => _setActiveMenu(sub)}
          close={close}
          cta={
            navItem.flyoutNavigation && navItem.flyoutNavigation.length !== 0
              ? navItem.navItemLink || navItem.ctaLink
              : null
          }
          ctaClass={linkClass}
          parentMenuName={level === 0 ? navItem.navItemText || navItem.navigationalTitle : parentMenuName}
        />
      )}
    </>
  );
};

export default CampaignNavItem;
